import { useI18n, Trans } from '~/common/context';
import { Button, Flex, Form, Icon, PaginationBar, Table, Text } from '@wyz/ui';
import { useBoolean, useDebouncedValue, useOnUpdate } from '@wyz/utils';
import clsx from 'clsx';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useManageCustomers } from '~/common/api';
import { IFeatureContext, IMatchedCustomer } from '~/common/types';
import { ENTITIES_STATUSES } from '~/common/schemas';
import {
  ICriteriaAwareForm,
  IOverwrittenMatchedCustomerCriteriaFilter,
} from '~/connect/types';
const ITEMS_PER_PAGE = 10;

type Props = {
  featureContext: IFeatureContext;
  matchUserContext?: boolean;
  renderCode?: (item: IMatchedCustomer) => React.ReactNode;
  name?: string;
  queryOverrideParams?: Partial<IOverwrittenMatchedCustomerCriteriaFilter>;
};

const MatchedCustomersTable = ({
  featureContext,
  matchUserContext = false,
  name = 'criteria',
  queryOverrideParams,
  renderCode,
}: Props) => {
  const { t } = useI18n();
  const [collapsed, handlers] = useBoolean(false);

  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchQuery, debounceHandlers] = useDebouncedValue('');
  const methods = useFormContext<ICriteriaAwareForm>();
  const watchedCriteria = useWatch({
    control: methods.control,
    name: name as unknown as 'criteria',
  });

  useOnUpdate(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, watchedCriteria]);

  const manageCustomers = useManageCustomers({
    paginateMatched: {
      args: {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        textQuery: searchQuery,
        statuses: [ENTITIES_STATUSES.ENABLED],
        criteria: watchedCriteria.map((associatedCriterion) => ({
          criterion: associatedCriterion.criterion.id,
          values: associatedCriterion.values,
        })),
        featureContext,
        matchUserContext,
        ...queryOverrideParams,
      },
    },
  });

  return (
    <>
      <Flex justifyContent='between' alignItems='center'>
        <Text heading='normal' color='secondary' weight='bold'>
          <Trans
            i18nKey='app.supplier_template.form.criteria.matched_count'
            values={{
              count: manageCustomers.paginateMatched.data?.meta.total || 0,
            }}
          />
          {manageCustomers.paginateMatched.isProcessing && (
            <Icon icon='spinner' width='1.25rem' className='mx-2' />
          )}
        </Text>
        <Button
          id='collapse'
          onClick={handlers.toggle}
          variant='link'
          size='sm'
        >
          {collapsed ? t('app.common.see_more') : t('app.common.see_less')}
          <Icon
            icon='arrow-left-s-line'
            className={clsx({
              'rotate-90': !collapsed,
              'rotate--90': collapsed,
            })}
            width='1.25rem'
            height='1.25rem'
          />
        </Button>
      </Flex>
      {!collapsed && (
        <div>
          <Flex justifyContent='between' alignItems='center'>
            <div className='my-2'>
              <Form.Control
                data-cy='search-matched-customers'
                type='search'
                placeholder={t('app.common.buttons.search')}
                size='sm'
                defaultValue={''}
                onChange={({ target }) =>
                  debounceHandlers.onChange(target.value)
                }
              />
            </div>
            <Text color='secondary-gray' heading='xs'>
              <Trans
                i18nKey='app.common.datatable.pagination.items_per_page_count'
                values={{
                  count: ITEMS_PER_PAGE,
                }}
              />
            </Text>
          </Flex>
          <Table bordered>
            <thead>
              <tr>
                <th>
                  <Text heading='sm' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.supplier_template.form.criteria.table.code' />
                  </Text>
                </th>
                <th>
                  <Text heading='sm' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.supplier_template.form.criteria.table.label' />
                  </Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {manageCustomers.paginateMatched.data?.data.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Text heading='sm' weight='semi-bold' color='secondary'>
                      {renderCode ? renderCode(item) : item.code}
                    </Text>
                  </td>
                  <td>
                    <Text
                      id={`matched-customer-name-${item.id}`}
                      heading='sm'
                      weight='normal'
                      color='secondary'
                    >
                      {item.name}
                    </Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <PaginationBar
            limit={ITEMS_PER_PAGE}
            total={manageCustomers.paginateMatched.data?.meta.total}
            page={currentPage}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </>
  );
};

export default MatchedCustomersTable;
